/** Sessão da pessoa logada. O token fica no navegador; a API confere a cada chamada. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Role, User } from '@dotapps/core';
import { api, beginViewAs, endViewAs, hasToken, setCarteira, setToken, setUnauthorizedHandler } from '../lib/api';
import { keys, useMe } from '../lib/queries';

export interface LoginResult {
  token: string;
  user: User;
  generatedPassword?: string;
}

interface AuthCtx {
  user: User | null;
  ready: boolean;
  isAdmin: boolean;
  role: Role | null;
  /** tem carteira de clientes (administrador ou vendedor) */
  isSeller: boolean;
  /** vê custo, margem e lucro (administrador; vendedor só da carteira dele, que é o que a API devolve) */
  canSeeCost: boolean;
  /** suporte da plataforma (dotapps) */
  isSupport: boolean;
  /** sessão do suporte visualizando como esta pessoa */
  viewAs: { by: string } | null;
  /** suporte: abre o sistema como outra pessoa (somente leitura) */
  startViewAs: (userId: string) => Promise<void>;
  /** volta para o acesso do suporte */
  stopViewAs: () => void;
  loginWithPassword: (email: string, password: string) => Promise<LoginResult>;
  loginWithGoogle: (credential: string) => Promise<LoginResult>;
  /** grava a sessão depois do login (separado para mostrar a senha gerada antes de entrar) */
  enter: (r: LoginResult) => void;
  logout: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [logged, setLogged] = useState(hasToken());
  const me = useMe(logged);

  const logout = useCallback(() => {
    // "Sair" durante a visualização do suporte volta ao acesso dele
    if (me.data?.viewAs && endViewAs()) {
      window.location.assign('/equipe');
      return;
    }
    setToken(null);
    setLogged(false);
    qc.clear();
  }, [qc, me.data?.viewAs]);

  // chaves no corpo: o efeito não pode devolver a função (o React a trataria como limpeza)
  useEffect(() => {
    setUnauthorizedHandler(logout);
  }, [logout]);

  const enter = useCallback(
    (r: LoginResult) => {
      setToken(r.token);
      qc.setQueryData(keys.me, r.user);
      setLogged(true);
    },
    [qc],
  );

  const startViewAs = useCallback(async (userId: string) => {
    const r = await api.post<LoginResult>(`/auth/view-as/${userId}`);
    beginViewAs(r.token);
    setCarteira('all');
    window.location.assign('/');
  }, []);

  const stopViewAs = useCallback(() => {
    if (!endViewAs()) setToken(null);
    setCarteira('all');
    window.location.assign('/equipe');
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      user: logged ? (me.data ?? null) : null,
      ready: !logged || !!me.data || me.isError,
      isAdmin: me.data?.role === 'admin',
      role: me.data?.role ?? null,
      isSeller: me.data?.role === 'admin' || me.data?.role === 'vendedor',
      canSeeCost: me.data?.role === 'admin' || me.data?.role === 'vendedor',
      isSupport: !!me.data?.support && !me.data?.viewAs,
      viewAs: me.data?.viewAs ?? null,
      startViewAs,
      stopViewAs,
      loginWithPassword: (email, password) => api.post<LoginResult>('/auth/login', { email, password }),
      loginWithGoogle: credential => api.post<LoginResult>('/auth/google', { credential }),
      enter,
      logout,
    }),
    [logged, me.data, me.isError, enter, logout, startViewAs, stopViewAs],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth fora do AuthProvider');
  return c;
}
